import React from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { startGoogleSignIn } from "@/lib/googleAuth";
import BrandLockup from "@/components/BrandLockup";
import NoIndex from "@/components/NoIndex";

const PANELS = [
  {
    to: "/admin/podcast",
    label: "Podcast episodes",
    blurb: "Add, edit and schedule episodes. Artwork, parsha, blurb, video.",
    testid: "admin-card-podcast",
  },
  {
    to: "/admin/announcement",
    label: "Announcement",
    blurb: "The gold bar across the top of every page. Edit it or switch it off.",
    testid: "admin-card-announcement",
  },
  {
    to: "/admin/venues",
    label: "Communities",
    blurb: "The scrolling list of communities beside the Community Events hero.",
    testid: "admin-card-venues",
  },
  {
    to: "/admin/live-events",
    label: "Live events",
    blurb: "Create a show, run the slides, moderate the audience queue.",
    testid: "admin-card-live",
  },
];

const goldBtn =
  "inline-flex items-center justify-center whitespace-nowrap rounded-full px-6 h-11 text-xs uppercase transition-colors hover:bg-[#F2D589]/[0.12]";
const goldStyle = {
  backgroundColor: "transparent",
  color: "#F2D589",
  border: "1px solid #F2D589",
  letterSpacing: "0.18em",
  fontWeight: 500,
};

export function AdminShell({ children, active }) {
  const { user } = useAuth();
  const showNav = Boolean(user?.is_admin);
  return (
    <div
      className="min-h-screen w-full"
      style={{ backgroundColor: "#080419", color: "#F5EFE3", fontFamily: "'Courier Prime', monospace" }}
      data-testid="admin-shell"
    >
      <NoIndex />
      <Toaster position="bottom-right" richColors />
      <div className="max-w-[900px] mx-auto px-6 sm:px-10 py-14">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <BrandLockup markSize={30} />
          {showNav ? (
          <nav className="flex flex-wrap items-center gap-6 text-xs uppercase" style={{ letterSpacing: "0.2em" }}>
            <Link to="/admin" style={{ color: active === "home" ? "#F2D589" : "#FCA07A" }} data-testid="admin-nav-home">
              Admin
            </Link>
            <Link
              to="/admin/podcast"
              style={{ color: active === "podcast" ? "#F2D589" : "#FCA07A" }}
              data-testid="admin-nav-podcast"
            >
              Podcast
            </Link>
            <Link
              to="/admin/announcement"
              style={{ color: active === "announcement" ? "#F2D589" : "#FCA07A" }}
              data-testid="admin-nav-announcement"
            >
              Announcement
            </Link>
            <Link
              to="/admin/venues"
              style={{ color: active === "venues" ? "#F2D589" : "#FCA07A" }}
              data-testid="admin-nav-venues"
            >
              Communities
            </Link>
            <Link
              to="/admin/live-events"
              style={{ color: active === "live" ? "#F2D589" : "#FCA07A" }}
              data-testid="admin-nav-live"
            >
              Live events
            </Link>
          </nav>
          ) : null}
        </div>
        {children}
      </div>
    </div>
  );
}

export default function AdminHome() {
  const { user, loading, logout } = useAuth();

  if (loading) {
    return (
      <AdminShell active="home">
        <p className="mt-20 text-sm" style={{ color: "#C6BBA9" }}>
          One moment…
        </p>
      </AdminShell>
    );
  }

  if (!user) {
    return (
      <AdminShell active="home">
        <div className="mt-16 max-w-[24rem]" data-testid="admin-signin">
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-2)" }}>
            Admin
          </h1>
          <p className="mt-4 text-sm" style={{ color: "#C6BBA9" }}>
            Sign in with an admin address.
          </p>
          <button
            type="button"
            onClick={() => startGoogleSignIn("/admin")}
            className={`${goldBtn} mt-8 w-full`}
            style={goldStyle}
            data-testid="admin-google-signin"
          >
            Continue with Google
          </button>
          <Link
            to="/app/login"
            state={{ from: "/admin" }}
            className="mt-6 block text-xs uppercase"
            style={{ color: "#FCA07A", letterSpacing: "0.2em" }}
            data-testid="admin-password-signin"
          >
            Use email and password &rarr;
          </Link>
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell active="home">
      {!user.is_admin ? (
        <div className="mt-20" data-testid="admin-home-denied">
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-2)" }}>
            Not your panel.
          </h1>
          <p className="mt-4 text-sm" style={{ color: "#C6BBA9" }}>
            {user.email} isn&apos;t on the admin list. If that&apos;s a mistake, ask Dan to add it.
          </p>
          <button
            type="button"
            onClick={async () => {
              try {
                await logout();
                toast.success("Signed out.");
              } catch (err) {
                toast.error(formatApiErrorDetail(err));
              }
            }}
            className="mt-8 text-xs uppercase"
            style={{ color: "#C6BBA9", letterSpacing: "0.2em" }}
            data-testid="admin-logout"
          >
            Sign out
          </button>
        </div>
      ) : (
        <>
      <div className="mt-14 flex flex-wrap items-end justify-between gap-6">
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-2)" }}>
          Admin
        </h1>
        <button
          type="button"
          onClick={async () => {
            try {
              await logout();
              toast.success("Signed out.");
            } catch (err) {
              toast.error(formatApiErrorDetail(err));
            }
          }}
          className="text-xs uppercase"
          style={{ color: "#C6BBA9", letterSpacing: "0.2em" }}
          data-testid="admin-logout"
        >
          Sign out ({user.email})
        </button>
      </div>

      <div className="mt-12 grid gap-6 sm:grid-cols-2" data-testid="admin-panels">
        {PANELS.map((p) => (
          <Link
            key={p.to}
            to={p.to}
            className="block px-8 py-10 transition-colors hover:border-[#F2D589]"
            style={{ border: "1px solid #241246", backgroundColor: "#0C0826" }}
            data-testid={p.testid}
          >
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "1.6rem" }}>
              {p.label}
            </h2>
            <p className="mt-4 text-sm" style={{ color: "#C6BBA9", lineHeight: 1.7 }}>
              {p.blurb}
            </p>
          </Link>
        ))}
      </div>
        </>
      )}
    </AdminShell>
  );
}
